import { CheckCircle, Eye, EyeOff, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import FlexibleInput from '../../components/Input/Input';
import { Error, Success } from '../../config/helpers/toast-helper';
import { useAffiliateUserStore } from '../../store/affiliate-user';

const ResetPassword = () => {
  const { resetPassword, loading } = useAffiliateUserStore();
  const navigate = useNavigate();

  // Form fields
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Field validation states
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  useEffect(() => {
    // Get email from localStorage if available
    const storedEmail = localStorage.getItem('affiliateResetEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  // Validation functions
  const validateEmail = (value) => {
    if (!value.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const validateCode = (value) => {
    if (!value.trim()) return 'Verification code is required';
    if (value.trim().length !== 4)
      return 'Verification code must be 4 characters';
    if (!/^[A-Za-z0-9]+$/.test(value.trim()))
      return 'Verification code must contain only letters and numbers';
    return '';
  };

  const validatePassword = (value) => {
    if (!value) return 'New password is required';
    if (value.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(value))
      return 'Password must include at least one uppercase letter';
    if (!/[a-z]/.test(value))
      return 'Password must include at least one lowercase letter';
    if (!/[0-9]/.test(value))
      return 'Password must include at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value))
      return 'Password must include at least one special character';
    return '';
  };

  const validateConfirmPassword = (value) => {
    if (!value) return 'Please confirm your password';
    if (value !== newPassword) return 'Passwords do not match';
    return '';
  };

  // Real-time validation
  useEffect(() => {
    const newErrors = {};
    if (touchedFields.email) newErrors.email = validateEmail(email);
    if (touchedFields.verificationCode)
      newErrors.verificationCode = validateCode(verificationCode);
    if (touchedFields.newPassword)
      newErrors.newPassword = validatePassword(newPassword);
    if (touchedFields.confirmPassword)
      newErrors.confirmPassword = validateConfirmPassword(confirmPassword);
    setErrors(newErrors);
  }, [email, verificationCode, newPassword, confirmPassword, touchedFields]);

  const handleBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleResetPassword = async () => {
    // Mark all fields as touched for validation
    const allTouched = {
      email: true,
      verificationCode: true,
      newPassword: true,
      confirmPassword: true,
    };
    setTouchedFields(allTouched);

    // Validate all fields
    const emailError = validateEmail(email);
    const codeError = validateCode(verificationCode);
    const passwordError = validatePassword(newPassword);
    const confirmError = validateConfirmPassword(confirmPassword);

    if (emailError || codeError || passwordError || confirmError) {
      setErrors({
        email: emailError,
        verificationCode: codeError,
        newPassword: passwordError,
        confirmPassword: confirmError,
      });
      Error('Please fix the form errors before submitting');
      return;
    }

    try {
      const payload = {
        email: email.trim().toLowerCase(),
        verificationCode: verificationCode.trim(),
        newPassword: newPassword,
      };

      const response = await resetPassword(payload);

      if (response?.success !== false) {
        Success(
          'Password reset successfully! You can now log in with your new password.'
        );
        localStorage.removeItem('affiliateResetEmail');
        navigate('/auth/login');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      Error(
        error?.response?.data?.message ||
          'Password reset failed. Please try again.'
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleResetPassword();
    }
  };

  const isFormValid =
    !validateEmail(email) &&
    !validateCode(verificationCode) &&
    !validatePassword(newPassword) &&
    !validateConfirmPassword(confirmPassword);

  // Password strength indicator
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-yellow-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-green-500', // For very strong, we can use the same color as strong
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-green-600 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-4xl font-bold text-white">
              Reset Password
            </h2>
            <p className="mt-2 text-sm text-gray-400">
              Enter your verification code and new password
            </p>
          </div>

          <div className="bg-gray-800/30 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-gray-700/50">
            <div className="space-y-6">
              <FlexibleInput
                placeholder="Email Address"
                type="email"
                value={email}
                disabled={!!localStorage.getItem('affiliateResetEmail')}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                icon={Lock}
                iconClass="text-green-400"
                error={errors.email}
                touched={touchedFields.email}
                className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
              />

              <FlexibleInput
                placeholder="Enter Code"
                type="text"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(
                    e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 4)
                  )
                }
                onBlur={() => handleBlur('verificationCode')}
                className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20 text-center text-xl tracking-[0.5em] font-mono"
                error={errors.verificationCode}
                touched={touchedFields.verificationCode}
              />

              <div className="relative">
                <FlexibleInput
                  placeholder="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onBlur={() => handleBlur('newPassword')}
                  onKeyPress={handleKeyPress}
                  icon={Lock}
                  iconClass="text-green-400"
                  error={errors.newPassword}
                  touched={touchedFields.newPassword}
                  className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20 pr-12"
                />
                <button
                  type="button"
                  onClick={toggleNewPasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors z-10"
                  style={{ top: '20px' }}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="space-y-2 relative z-20">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Password Strength</span>
                    <span
                      className={`font-medium ${
                        passwordStrength >= 4
                          ? 'text-green-400'
                          : passwordStrength >= 3
                          ? 'text-blue-400'
                          : passwordStrength >= 2
                          ? 'text-yellow-400'
                          : 'text-red-400'
                      }`}
                    >
                      {strengthLabels[passwordStrength]}
                    </span>
                  </div>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((level) => {
                      {
                        console.log(
                          `Password strength level ${level}, passwordStrength ${passwordStrength}`
                        );
                      }
                      return (
                        <div
                          key={level}
                          className={`h-2 flex-1 rounded-full transition-colors duration-300 ${
                            level <= passwordStrength
                              ? strengthColors[passwordStrength]
                              : 'bg-gray-600'
                          }`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="relative">
                <FlexibleInput
                  placeholder="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => handleBlur('confirmPassword')}
                  onKeyPress={handleKeyPress}
                  icon={Lock}
                  iconClass="text-green-400"
                  error={errors.confirmPassword}
                  touched={touchedFields.confirmPassword}
                  className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20 pr-12"
                />
                <button
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors z-10"
                  style={{ top: '20px' }}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
                {confirmPassword && newPassword === confirmPassword && (
                  <div
                    className="absolute right-12 top-1/2 transform -translate-y-1/2 text-green-400 z-10"
                    style={{ top: '20px' }}
                  >
                    <CheckCircle className="h-5 w-5" />
                  </div>
                )}
              </div>

              <Button
                text="Reset Password"
                onClick={handleResetPassword}
                loading={loading}
                disabled={!isFormValid}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0 shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300"
                size="large"
              />

              <div className="text-center">
                <p className="text-sm text-gray-400">
                  Remember your password?{' '}
                  <Link
                    to="/auth/login"
                    className="text-green-400 hover:text-green-300 font-medium transition-colors"
                  >
                    Back to Login
                  </Link>
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500 max-w-sm mx-auto">
              Your new password will be encrypted and stored securely
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
